<template>
    <app-layout
        :silent-login-enabled="config.silentLoginEnabled"
        :error-boundary-options="errorBoundaryOptions"
        :v-app-options="vAppOptions"
        hide-footer
    >
        <template v-if="mainStore.ready" #sidebar>
            <app-drawer v-model="showAppDrawer" :full-height="drawerIsFullHeight" class="tp-app__drawer">
                <div id="appDrawerPrepend" />
                <router-view :key="route.path" name="appDrawer" />
            </app-drawer>
        </template>

        <v-main v-if="mainStore.ready" class="d-flex flex-column align-center justify-center w-100">
            <v-container fluid class="pa-0 h-100" style="position: relative">
                <router-view name="main" />
            </v-container>
        </v-main>

        <v-dialog v-if="mainStore.isDevMode" v-model="dialogIsVisible" max-width="500">
            <template #activator="{ props: activatorProps }">
                <div class="position-absolute top-0 right-0 pa-2" style="z-index: 1000">
                    <v-btn v-bind="activatorProps" icon color="warning">
                        <v-icon icon="mdi-xml" size="32" />
                    </v-btn>
                </div>
            </template>
            <dev-mode-configurator
                v-model="mainStore.devModeConfiguration"
                @update:model-value="dialogIsVisible = false"
                @cancel="dialogIsVisible = false"
                @reset="resetConfig"
            />
        </v-dialog>

        <v-dialog v-model="showDevModeNotification" max-width="500" persistent>
            <v-card>
                <v-card-title class="pt-4 px-6 pt-md-6">
                    <h1 class="text-h3">Developer Mode Activated</h1>
                </v-card-title>
                <v-card-text class="text-h6 font-weight-regular py-0">
                    <p class="my-2">Developer Mode allows you to change various settings that affect how this app functions.</p>
                    <p class="my-2">
                        All changes are persistent for the current session. To disable Developer Mode, simply open the app in a new tab.
                    </p>
                </v-card-text>
                <v-card-actions class="px-4 pb-4 px-md-6">
                    <v-spacer />
                    <v-btn color="primary" @click="showDevModeNotification = false">Understood</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </app-layout>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { computed, getCurrentInstance, nextTick, onMounted, provide, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/features/common/stores';
import { AppLayout } from '@geta/kolumbus-frontend/components';
import { AppDrawer, DevModeConfigurator } from '@/features/common/components';
import { useDevModeActivator } from '@/features/common/composables';
import { AppProvideKey } from './injectionKeys';
import { createGtm } from 'vue-gtm';
import { useEventListener } from '@vueuse/core';
import { useBookingFeature } from './features/bookings/composables';
import { useDisplay } from 'vuetify';
import { useDeparturesSearchStore } from './features/departures/stores';
import { useTripSearchStore } from './features/trips/stores';
import { routeNames } from './router';
import { useAppStore } from '@geta/kolumbus-frontend/features/common';

const errorBoundaryOptions = {
    errorsToDisplay: ['PlacesError', 'GeolocationError', 'BookingError', 'ProfileError', 'SearchError']
};

const vAppOptions = computed(() => ({
    class: ['tp-app', ...cssClasses.value]
}));

useBookingFeature();
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const mainStore = useMainStore();
const tripSearchStore = useTripSearchStore();
const departuresSearchStore = useDeparturesSearchStore();
const { language } = storeToRefs(appStore);
const { showAppDrawer, config } = storeToRefs(mainStore);
const { mobile: isMobile } = useDisplay();
const { dialogIsVisible, showDevModeNotification, init: initDevModeActivator, resetConfig } = useDevModeActivator();

const cssClasses = ref<string[]>([]);
const currentTime = ref(new Date());
const drawerIsFullHeight = ref(true);

function setDrawerFullHeight(fullHeight: boolean) {
    drawerIsFullHeight.value = fullHeight;
}

provide(AppProvideKey, { currentTime, setDrawerFullHeight });

watch(language, async (value, oldValue) => {
    if (value !== oldValue) {
        try {
            await router.replace({ params: { locale: value }, query: route.query });
        } catch (e) {
            appStore.registerError(e);
            router.push('/');
        }
    }
});

const instance = getCurrentInstance();

onMounted(async () => {
    // start clock
    setInterval(() => (currentTime.value = new Date()), 1000);

    // init
    initDevModeActivator();

    // add event listener to add/remove focused class whenever an input field is focused
    useEventListener(
        document,
        ['focus', 'blur'],
        e => {
            if ((e.target as Element).nodeName !== 'INPUT') return;

            if (e.type === 'focus') {
                cssClasses.value.push('tp-app--has-focused');
            } else if (e.type === 'blur') {
                cssClasses.value = cssClasses.value.filter(x => x !== 'tp-app--has-focused');
            }
        },
        true
    );

    // set up gtm
    if (instance && config.value?.googleTagManagerConfig?.id) {
        instance.appContext.app.use(createGtm({ id: config.value.googleTagManagerConfig.id }));
    }

    // set up router guard to reset stores when navigating away from trip search or departures search
    router.afterEach((to, from) => {
        // reset stores if navigating away from trip search or departures search
        // but not if navigating to booking details
        if (to.name !== from.name && to.name !== routeNames.bookingDetails) {
            tripSearchStore.$reset();
            departuresSearchStore.$reset();
        }
    });

    mainStore.ready = true;
});

// reset app drawer when going from desktop to mobile
watch(
    isMobile,
    value => {
        if (!value) return;

        nextTick(() => (showAppDrawer.value = true));
    },
    { immediate: true }
);
</script>

<style lang="scss">
.tp-app {
    &__header {
        z-index: 1000;
    }

    &__drawer {
        z-index: 900;
    }

    &__banner {
        position: absolute;
        right: 0;
        z-index: 1000;
        width: calc(100% - var(--v-layout-left));
    }
}

.v-main {
    z-index: 1;
}
</style>
